<script lang="ts">
	import ProductStars from '$lib/components/product/ProductStars.svelte'
	import ProductDeliveryTimes from '$lib/components/product/ProductDeliveryTimes.svelte'
	import { PUBLIC_BIXOTO_MEDIA } from '$env/static/public'
	import { addToCart } from '$lib/js/cart'
	import {
		getProductFinalPriceWithTax,
		getProductRegularPriceWithTax,
		getProductRetailPriceWithTax,
		getProductSpecialPriceWithTax,
		is_set_array,
	} from '$lib/js/helpers'
	import { goto } from '$app/navigation'
	import { t } from 'svelte-i18n-lingui'
	import type { CartProduct } from '$lib/js/types'
	import type { BaseProduct, FullProduct, GalleryEntry } from '$lib/js/elasticsearch/types'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { getFormattedPrice } from '$lib/js/helpers/prices'


	export let product: BaseProduct | FullProduct
	export let type: '' | 'eco' | 'search' | string = ''
	export let lazy = false
	const scope = getContextScope()

	let alt_text = `${product.name} Precio: ${getProductFinalPriceWithTax(product)}. SKU: ${product.sku}`

  let retail_price = getProductRetailPriceWithTax(product)
	let special_price = getProductSpecialPriceWithTax(product)
	let regular_price = getProductRegularPriceWithTax(product)

	async function addItemToCart(product: CartProduct) {
		if (product.type_id === 'simple' && product.stock.source_quantity > 0) {
			product.cart_qty = 1
			await addToCart(product)
		} else {
			await goto(`/${scope}/p/${product.url_key}`)
		}
	}

	$: innerWidth = 0

	let imgIndex = 0
	let hovering = false
	let productGallery: GalleryEntry[] = []

	if ((<FullProduct>product).media_gallery && (<FullProduct>product).media_gallery.length > 1) {
		productGallery = (<FullProduct>product).media_gallery.filter(entry => !entry.vid && entry.resized_small_image)
	}

	function showSecondImg() {
		if (is_set_array(productGallery) && productGallery.length > 1 && innerWidth > 768) {
			hovering = true
			imgIndex = 1
		}
	}

	function hideSecondImg() {
		hovering = false
		imgIndex = 0
	}

	function prevImg() {
		imgIndex = imgIndex !== 0 ? (imgIndex - 1) : (productGallery.length - 1)
	}

	function nextImg() {
		imgIndex = imgIndex === (productGallery.length - 1) ? 0 : (imgIndex + 1)
	}

	let hoverClass = is_set_array(productGallery) && productGallery.length > 1 ? '' : 'hover'

	const more_than_one_image = is_set_array(productGallery) && productGallery.length > 1 && product?.resized_small_image
</script>


<svelte:window bind:innerWidth />

<div class="product-card {type} {hoverClass}">
	<div class="product-card__inner">
		<div class="product-card__top">
			<a href="/{scope}/p/{product.url_key || product.slug}" class="product-card__img" role="button" tabindex="0"
				 on:mouseenter={more_than_one_image ? () => showSecondImg() : null}
				 on:mouseleave={more_than_one_image ? () => hideSecondImg() : null}
			>
				{#if more_than_one_image}
					{#if hovering === true}
						<div class="slider-arrows">
							<button class="icon icon-left" on:click|preventDefault={() => prevImg()}></button>
							<button class="icon icon-right" on:click|preventDefault={() => nextImg()}></button>
						</div>
					{/if}
					<img
						src="{PUBLIC_BIXOTO_MEDIA}{productGallery[imgIndex].resized_small_image.path}"
						alt="{product.name}"
						title="{product.name}"
						width="{product.resized_small_image?.width}"
						height="{product.resized_small_image?.height}"
					>
				{:else if product.resized_small_image}
					<img src="{PUBLIC_BIXOTO_MEDIA}{product.resized_small_image.path}"
							 loading="{lazy ? 'lazy' : null}"
							 height="{product.resized_small_image?.height}"
							 width="{product.resized_small_image?.width}"
							 alt="{alt_text}"
							 title="{product.name}">
				{:else if product?.image_path}
					<img src="{PUBLIC_BIXOTO_MEDIA}{product.image_path}"
							 loading="{lazy ? 'lazy' : null}"
							 height="210"
							 width="210"
							 alt="{alt_text}"
							 title="{product.name}">
				{:else if product.image && product.image !== 'no_selection'}
					<img src={`${PUBLIC_BIXOTO_MEDIA}${(product.image)}`} alt="{alt_text}" title="{product.name}">
				{:else}
					<img src="/images/no-image.jpg" alt="{$t`Image not available`}" title="{$t`Image not available`}"
							 height="210" width="210">
				{/if}

				{#if product.stock?.source_quantity === 0}
					<p class="no-stock">{$t`No stock`}</p>
				{/if}
			</a>

			<div class="product-card__info">
				<a href="/{scope}/p/{product?.url_key || product?.slug}">
					<h3 class="product-card__name">{product.name}</h3>

					{#if product.rating_average}
						<div class="product-card__rating">
							<ProductStars {product} />
						</div>
					{/if}
				</a>

				<div class="price">
					{#if special_price && special_price < regular_price}
            <p class="original">{getFormattedPrice(regular_price)}€</p>
            <p class="special">{getFormattedPrice(special_price)}€</p>
					{:else if regular_price < retail_price}
						<p class="original">{getFormattedPrice(retail_price)}€</p>
						<p class="special">{getFormattedPrice(regular_price)}€</p>
					{:else}
            <p class="special">{getFormattedPrice(regular_price)}€</p>
          {/if}
				</div>
			</div>
		</div>

		{#if type !== 'search'}
			<div class="product-card__bottom">
				<ProductDeliveryTimes type="product" {product} />

				<button
					class="to-cart"
					on:click={() => addItemToCart(product)}
					aria-label="{$t`Add to cart`}"
				>
					<span class="icon icon-add-to-cart"></span>
				</button>
			</div>
		{/if}
	</div>
</div>


<style lang="scss">
  // .special-promo {
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   position: absolute;
  //   // top: -15px;
  //   // left: -20px;
  //   bottom: -15px;
  //   right: -10px;
  //   transform: rotate(-6deg);
  //   width: 110px;
  //   height: 65px;

  //   @media (min-width: 769px) {
  //     width: 140px;
  //     height: 80px;
  //   }

  //   img {
  //     position: absolute;
  //     width: 100%;
  //   }

  //   p {
  //     position: relative;
  //     color: white;
  //     font-size: 14px;
  //     font-weight: 900;
  //     line-height: 1;
  //     text-align: center;
  //     letter-spacing: -0.05em;
  //     margin-bottom: 0;

  //     @media (min-width: 769px) {
  //       font-size: 16px;
  //     }
  //   }
  // }

  .slider-arrows {
    position: absolute;
    display: flex;
    justify-content: space-between;
    left: 0;
    right: 0;
    top: calc(50% - 8px);

    .icon {
      display: block;
      padding: 20px;
      margin: -20px -30px;
      z-index: 1;
    }
  }

  .product-card {
    height: calc(100% - 30px);

    &.hover {
      &:hover {
        .product-card__img {
          img:not(.promo-img) {
            transform: none;
          }
        }
      }

      .product-card__img {
        img {
          @media (min-width: 769px) {
            transform: scale(0.93);
          }
        }
      }
    }

    &__img {
      display: block;
      width: 110px;
      padding-bottom: 110px;
      position: relative;
      background: white;

      @media (min-width: 340px) {
        width: 140px;
        padding-bottom: 140px;
      }

      @media (min-width: 450px) {
        width: 100%;
        padding-bottom: 100%;
        margin-bottom: 10px;
      }

      img:not(.promo-img) {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        object-fit: contain;
        width: 110px;
        height: 110px;
        transition: all linear 0.15s;

        @media (min-width: 340px) {
          width: 140px;
          height: 140px;
        }

        @media (min-width: 450px) {
          width: 100%;
          height: auto;
        }
      }

      .no-stock {
        position: absolute;
        left: 0;
        bottom: 5px;
        background: white;
        padding: 3px 12px;
        color: var(--main);
        border: 1px solid var(--main);
      }
    }

    &__rating {
      display: flex;
      margin-bottom: 10px;
    }

    &__name {
      word-break: break-word;
      text-align: left;
    }

    &__inner {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 15px;
      background: white;
      // border-radius: 12px;
      box-shadow: 0px 0px 17px 5px rgba(0, 0, 0, 0.06);
      height: 100%;

      h3 {
        font-size: 14px;
        font-weight: 400;
        margin-bottom: 10px;
      }
    }

    .price {
      color: black;

      @media (min-width: 450px) {
        margin-bottom: 12px;
      }

      .original {
        color: var(--grey--b);
        text-decoration: line-through;
      }

      .special {
        font-size: 16px;
        font-weight: 900;
      }
    }

    &__top {
      @media (max-width: 449px) {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
      }
    }

    &__info {
      @media (max-width: 449px) {
        width: calc(100% - 152px);
      }

      @media (max-width: 339px) {
        width: calc(100% - 122px);
      }
    }

    &__bottom {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;

      .to-cart {
        transition: all linear 0.125s;
        background: var(--main);
        border: 2px solid var(--main);
        padding: 8px 8px 6px;
        margin: 0 -15px -15px 15px;
        // border-top-left-radius: 6px;
        // border-bottom-right-radius: 12px;

        .icon {
          font-size: 20px;
          font-weight: 400;
          color: white;
        }
      }
    }

    &.slider,
    &.search,
    &.eco {
      .product-card {
        &__inner {
          .price {
            margin-bottom: 12px;
          }
        }

        &__img {
          width: 100%;
          padding-bottom: 100%;
          margin-bottom: 10px;

          img:not(.promo-img) {
            width: 100%;
            height: auto;
          }
        }

        &__top {
          display: block;
        }

        &__info {
          width: 100%;
        }
      }
    }

    &.eco {
      background: rgba(255, 255, 255, 0.80);

      .product-card {
        &__inner {
          box-shadow: none;
          padding: 12px;

          .to-cart {
            margin: 0 -12px -8px 12px;
            border-color: var(--secondary);
            background: var(--secondary);
          }
        }
      }
    }

    &.slider {
      margin: 15px 5px;

      .product-card {
        &__inner {
          height: calc(100% - 30px);
        }
      }
    }

    &.search {
      .product-card {
        &__inner {
          .price {
            margin-bottom: 0;
          }
        }
      }
    }
  }
</style>
